import React from 'react';
import { ArrowUpRight, Clock, ShieldCheck, Sparkles } from 'lucide-react';
import { RevealSection, RevealCard } from '../Animations';

const CAL_URL = 'https://calendly.com/emergeautomations/new-meeting';

const benefits = [
  { icon: Clock, label: '15 minutes', detail: 'Focused working session, not a sales pitch.' },
  { icon: Sparkles, label: 'Free audit', detail: 'Leave with a concrete plan you could act on today.' },
  { icon: ShieldCheck, label: 'No commitment', detail: 'We only continue if the ROI is obvious.' },
];

const Booking = () => {
  return (
    <section id="calendly-booking" className="section-padding relative">
      <div className="container-custom">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12 md:mb-16">
          <RevealSection className="lg:col-span-7">
            <span className="eyebrow">04. Schedule</span>
            <div className="mt-3 mb-8 h-px bg-white/10" />
            <h2 className="text-[clamp(2rem,4.5vw,3.5rem)] font-display font-semibold text-white leading-[1.05] tracking-[-0.03em]">
              Ready to start?
            </h2>
          </RevealSection>
          <RevealSection className="lg:col-span-5">
            <p className="text-[17px] text-zinc-400 leading-[1.65] max-w-xl">
              Book your free 15-minute AI strategy audit. We'll find the best automation opportunities for your business.
            </p>
          </RevealSection>
        </div>

        <RevealCard className="relative rounded-[var(--radius-xl)] border border-white/10 bg-[color:var(--color-surface-1)] overflow-hidden">
          {/* Ambient accent halo */}
          <div
            className="pointer-events-none absolute -top-40 -right-20 w-[560px] h-[560px] rounded-full"
            style={{
              background:
                'radial-gradient(closest-side, rgba(198,242,78,0.14), transparent 70%)',
              filter: 'blur(8px)',
            }}
            aria-hidden="true"
          />

          <div className="relative grid lg:grid-cols-12 gap-10 p-8 md:p-14">
            {/* Left — pitch + CTA */}
            <div className="lg:col-span-7 flex flex-col">
              <span className="live-pill mb-6 self-start">
                <span className="live-pill__dot" />
                <span>Next slot · This week</span>
              </span>

              <h3 className="text-[clamp(1.75rem,3vw,2.5rem)] font-display font-semibold text-white leading-[1.1] tracking-[-0.025em] mb-4">
                Book a free 15-minute AI audit.
              </h3>
              <p className="text-[16px] text-zinc-400 leading-relaxed mb-8 max-w-lg">
                A working call to map out your highest-ROI automation. You'll leave with a concrete plan whether we build it together or not.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <a
                  href={CAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Book your free AI audit
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                </a>
                <a href="mailto:emergeautomations@gmail.com" className="btn-secondary">
                  Prefer email?
                </a>
              </div>

              <div className="mt-auto pt-6 border-t border-white/5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                <span data-mono className="uppercase tracking-[0.12em] text-zinc-500">
                  Trusted by
                </span>
                <span className="text-zinc-400">SMEs across UK &amp; USA</span>
                <span className="w-1 h-1 rounded-full bg-zinc-700" />
                <span className="text-zinc-400">10,000+ hours saved</span>
                <span className="w-1 h-1 rounded-full bg-zinc-700" />
                <span className="text-zinc-400">99.9% uptime</span>
              </div>
            </div>

            {/* Right — benefit stack */}
            <div className="lg:col-span-5">
              <div className="rounded-[var(--radius-lg)] border border-white/10 bg-[color:var(--color-bg-base)] overflow-hidden">
                {benefits.map((b, i) => (
                  <div
                    key={b.label}
                    className={`flex items-start gap-4 p-5 md:p-6 ${
                      i !== benefits.length - 1 ? 'border-b border-white/5' : ''
                    }`}
                  >
                    <div className="w-9 h-9 rounded-[var(--radius-sm)] border border-white/10 bg-white/[0.02] flex items-center justify-center shrink-0">
                      <b.icon
                        className="w-4 h-4 text-[color:var(--color-accent)]"
                        strokeWidth={1.75}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-[15px] font-semibold text-white tracking-[-0.01em]">
                        {b.label}
                      </p>
                      <p className="text-sm text-zinc-400 leading-relaxed mt-0.5">
                        {b.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RevealCard>
      </div>
    </section>
  );
};

export default Booking;
